import { useQuery } from '@tanstack/react-query';
import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import useSiteState from '@app/state/SiteState';
import Month from '@app/model/market_state/Month';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import type { Pickup, PickupWindow } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/PickupCell.utilities';

export type PmsPoint = { daysAgo: number; sourceId: number | null; capturedAt: string | null; availability: number | null;
 onTheBooks: number | null; occupancy: number | null; pickup: number | null; issues: string[]; rooms: { hotelId: number | null; name: string; availability: number | null }[] };
export type PmsLastPriceChange = { changedAt: string; daysAgo: number; pickup: number | null; issues: string[] };
export type PmsScope = { roomMappingType: HotelRoomMappingType | null; capacity: number | null; issues: string[]; points: PmsPoint[]; lastPriceChange: PmsLastPriceChange | null };
export type PmsDay = { statDate: string; scopes: PmsScope[] };
export type PmsMetrics = { asOf: string; days: PmsDay[] };
export const pmsScope = (scopes: PmsScope[] | undefined, type?: HotelRoomMappingType) => scopes?.find(s => (s.roomMappingType ?? undefined) === type);
export const pmsPoint = (scope: PmsScope | undefined, days = 0) => scope?.points.find(p => p.daysAgo === days);
export const pmsPickup = (scopes: PmsScope[] | undefined, type?: HotelRoomMappingType): Pickup | undefined => {
 const scope = pmsScope(scopes, type);
 if (!scope) return undefined;
 return { byWindow: { ...Object.fromEntries([3,7,15,30].map(n => [n, pmsPoint(scope, n)?.pickup ?? undefined])),
   0: scope.lastPriceChange?.pickup ?? undefined } as Partial<Record<PickupWindow, number>>,
  booked: pmsPoint(scope)?.onTheBooks ?? undefined, capacity: scope.capacity ?? undefined,
  lastChange: scope.lastPriceChange };
};
const query = { name: 'findCompanyHotelPmsMetrics', gql: `query findCompanyHotelPmsMetrics($companyHotelId: Long!, $year: Int!, $month: Month!) {
 findCompanyHotelPmsMetrics(companyHotelId: $companyHotelId, year: $year, month: $month) {
  asOf days { statDate scopes { roomMappingType capacity issues lastPriceChange { changedAt daysAgo pickup issues } points { daysAgo sourceId capturedAt availability onTheBooks occupancy pickup issues rooms { hotelId name availability } } } }
 }
}` };
export const usePmsMetrics = (companyHotelId: number, year: number, month: Month, enabled = true) => {
 const principal = useSiteState(s => s.principal);
 return useQuery<PmsMetrics>({
  queryKey: [...PricingAutomationQueryKeys.pmsMetrics(companyHotelId), year, month, principal?.id, principal?.pricingAuthority, principal?.isEnabled],
  queryFn: async ({ signal }) => {
   const result = await GraphQLExecutor.execute<PmsMetrics>(query, { companyHotelId, year, month });
   signal.throwIfAborted();
   return result;
  },
  enabled: enabled && companyHotelId > 0,
  staleTime: 0,
  refetchOnMount: true,
  structuralSharing: (previous, next) => previous && (previous as PmsMetrics).asOf > (next as PmsMetrics).asOf ? previous : next,
 });
};
export const usePmsMetricsForDate = (companyHotelId: number, date: string) => {
 const [year, month] = date.split('-').map(Number);
 const months = Object.values(Month);
 return usePmsMetrics(companyHotelId, year, months[month - 1]);
};
